import {Link, useNavigate, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {Alert, Box, Button, Container, IconButton, InputAdornment, Paper, Skeleton, TextField, Typography} from "@mui/material";
import Info from "../../../components/home/share/info";
import Actions from "../../../components/home/share/actions";
import Content from "../../../components/home/share/content";
import {getShareFiles, getShareFilesInfo, getShareFolder} from "../../../lib/services/drive";
import Toasty from "../../../theme/components/Toasty";
import {useUser} from "../../../lib/hooks";
import LoginModal from "../../../components/common/loginModal";
import {ErrorOutline, LoginRounded, PasswordRounded, VisibilityOffRounded, VisibilityRounded} from "@mui/icons-material";
import {LoadingButton} from "../../../theme/components/LoadingButton";
import {Helmet} from "react-helmet";

const Share = () => {

    const user = useUser();
    const {shareFolderId} = useParams();

    const [files, setFiles] = useState([]);
    const [shareInfo, setShareInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [contentLoading, setContentLoading] = useState(true);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [isPasswordRequired, setIsPasswordRequired] = useState(false);
    const [isLoginRequired, setIsLoginRequired] = useState(false);
    const [isAccessDenied, setIsAccessDenied] = useState(false);
    const navigate = useNavigate();

    async function getShareFolderInfo(options = {}) {
        const {enteredPassword, isLoggedIn} = options;
        const {data} = await getShareFilesInfo(shareFolderId);
        if (data?.isSuccess) {
            setShareInfo(data.value);
            const {isExpired, isNeedRegister, isPassword} = data.value;
            let isError = false;
            if (isExpired) return setIsExpired(true);
            if (isNeedRegister && !user && !isLoggedIn) {
                setIsLoginRequired(true);
                setLoginModalOpen(true);
                isError = true;
            }
            if (isPassword && !enteredPassword) {
                setIsPasswordRequired(true);
                isError = true;
            }
            if (isError) return;
            setIsPasswordRequired(false);
            setIsLoginRequired(false);
            const {data: shareFolderData} = await getShareFolder(shareFolderId, enteredPassword);
            if (shareFolderData?.isSuccess) {
                const payload = shareFolderData.value;
                const newFiles = [];
                if (payload.length) {
                    payload.forEach( async (i) => {
                        const fileType = i.dF_FileTitle.split('.').pop();
                        newFiles.push({
                            id: i.dF_ID,
                            name: i.dF_FileTitle,
                            title: i.dF_FileName,
                            description: i.dF_Description,
                            type: fileType,
                            price: i.dF_FilePrice,
                            folderId: i.dF_FolderID,
                            isShared: i.dF_IsShared,
                            shareType: i.dF_ShareType,
                            size: i.dF_Size,
                            fileHashId: i.fileHashId,
                            tags: i.tags,
                            downloadCount: i.dF_FileDownloadCount,
                            likesCount: i.dF_FileLikeCount,
                        })
                    });
                }
                setFiles(newFiles);
                setContentLoading(false);
            } else {
                if (shareFolderData && shareFolderData.errors?.length) {
                    // maybe its sharedFile?!, the errors doesn't have status code
                    if (shareFolderData.errors.includes("شما به این اشتراک گذاری دسترسی ندارید")) {
                        const {data: shareFilesData} = await getShareFiles(shareFolderId, enteredPassword);
                        if (shareFilesData?.isSuccess) {
                            const payload = shareFilesData.value;
                            const newFiles = [];
                            if (payload.length) {
                                payload.forEach(async (i) => {
                                    const fileType = i.dF_FileTitle.split('.').pop();
                                    newFiles.push({
                                        id: i.dF_ID,
                                        name: i.dF_FileTitle,
                                        title: i.dF_FileName,
                                        description: i.dF_Description,
                                        type: fileType,
                                        price: i.dF_FilePrice,
                                        folderId: i.dF_FolderID,
                                        isShared: i.dF_IsShared,
                                        shareType: i.dF_ShareType,
                                        size: i.dF_Size,
                                        fileHashId: i.fileHashId,
                                        tags: i.tags,
                                        downloadCount: i.dF_FileDownloadCount,
                                        likesCount: i.dF_FileLikeCount,
                                    })
                                });
                            }
                            setFiles(newFiles);
                            setContentLoading(false);
                        } else {
                            if (shareFilesData && shareFilesData.errors?.length) {
                                if (shareFilesData.errors.includes("شما به این اشتراک گذاری دسترسی ندارید")) {
                                    return setIsAccessDenied(true);
                                }
                                shareFilesData.errors.map(i => Toasty.error(i));
                            }
                        }
                        return;
                    }
                    shareFolderData.errors.map(i => Toasty.error(i));
                }
            }
        } else {
            if (data && data.errors?.length) {
                if (data.errors.includes('برای مشاهده این اشتراک گذاری نیاز به ورود به سیستم دارید')) {
                    setIsLoginRequired(true);
                    return setLoginModalOpen(true);
                }
                if (data.errors.includes("لینک منقضی شده است")) {
                    return setIsExpired(true);
                }
                data.errors.map(i => Toasty.error(i));
            }
        }
    }

    useEffect(() => {
        if (shareFolderId) {
            getShareFolderInfo().then(() => setLoading(false))
                .catch((err) => {
                    console.log(err);
                    Toasty.error('خطایی غیر منتظره رخ داد')
                })
        } else navigate('404');
    }, [shareFolderId]);


    return (
        <>
            <Helmet>
                <title>مشاهده اشتراک گذاری - درایو میلفا</title>
            </Helmet>
            {!loading
                ?
                !isLoginRequired || user
                    ?
                    <Container sx={{ my: 5, display: "flex", justifyContent: "space-between", flexDirection: {xs: "column", lg: "row"} }}>
                        {!isExpired
                            ?
                            !isAccessDenied
                                ?
                                isPasswordRequired
                                    ?
                                    <EnterPassword
                                        onSubmit={(enteredPassword) => getShareFolderInfo({enteredPassword})}
                                        setIsAccessDenied={setIsAccessDenied}/>
                                    :
                                    !contentLoading
                                        ?
                                        <>
                                            <Box component="main" flex={1}>
                                                <Content files={files} shareInfo={shareInfo}/>
                                                {/*<Comments/>*/}
                                            </Box>
                                            <Box component="aside" sx={{ mt: { xs: 5, lg: 0 }, ml: { lg: 5 }, width: {xs: "100%", lg: "25%"} }}>
                                                <Info files={files} data={shareInfo}/>
                                                <Actions files={files} linkAddress={shareFolderId}/>
                                            </Box>
                                        </>
                                        :
                                        <LoadingSkeleton/>
                                :
                                <AccessDenied/>
                            :
                            <ExpiredError/>
                        }
                    </Container>
                    :
                    <LoginToContinue onClick={() => setLoginModalOpen(true)}/>
                :
                <LoadingSkeleton/>}
            {/* Login Modal */}
            <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} options={{ noReload: true, onSuccess: () => getShareFolderInfo({isLoggedIn: true})}}/>
        </>
    )
}
export default Share;

const EnterPassword = ({onSubmit, setIsAccessDenied}) => {

    const {shareFolderId} = useParams();

    const [enteredPassword, setEnteredPassword] = useState('');
    const [showEnteredPassword, setShowEnteredPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [pending, setPending] = useState(false);

    const handleOnSubmit = async (e) => {
        e.preventDefault();
        if (!enteredPassword) return setPasswordError('رمز عبور به درستی وارد نشده!');
        setPending(true);
        try {
            const {data} = await getShareFolder(shareFolderId, enteredPassword);
            if (data?.isSuccess) {
                onSubmit(enteredPassword);
            } else {
                if (data && data.errors?.length) {
                    if (data.errors.includes("شما به این اشتراک گذاری دسترسی ندارید")) {
                        const {data: shareFilesData} = await getShareFiles(shareFolderId, enteredPassword);
                        if (shareFilesData?.isSuccess) {
                            onSubmit(enteredPassword);
                        } else {
                            if (shareFilesData && shareFilesData.errors?.length) {
                                if (shareFilesData.errors.includes("شما به این اشتراک گذاری دسترسی ندارید")) {
                                    return setIsAccessDenied(true);
                                }
                                shareFilesData.errors.map(i => Toasty.error(i));
                            }
                        }
                        return;
                    }
                    data.errors.map(i => Toasty.error(i));
                }
            }
        } catch (err) {
            console.log(err);
            Toasty.error("خطایی غیر منتظره رخ داد")
        }
        setPending(false);
    }

    return (
        <Paper sx={{ p: 2.5, width: 1, minHeight: {xs: "50vw", lg: "25vw"}, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <Box display="flex" alignItems="center" justifyContent="center">
                <PasswordRounded color="warning" sx={{ fontSize: "2.5rem" }}/>
                <Typography ml={1} fontSize={{ xs: "1.1rem", lg: "1.25rem" }} fontWeight={600}>رمز عبور</Typography>
            </Box>
            <Alert severity="warning" sx={{ mt: 1.5 }}>برای دسترسی به این اشتراک گذاری لطفا رمز را وارد کنید</Alert>
            <Box component="form" onSubmit={handleOnSubmit} mt={{lg: 2}} width={1} display="flex" alignItems="center" justifyContent="center" flexDirection={{xs: "column", lg: "row" }}>
                <TextField
                    value={enteredPassword} onChange={(e) => setEnteredPassword(e.target.value)} required
                    name="enteredPassword" type={showEnteredPassword ? 'text' : 'password'}
                    error={!!passwordError}
                    label='رمز' placeholder="رمز اشتراک گذاری را وارد کنید"
                    sx={{ mt: {xs: 2, lg: 0}, width: {xs: "100%", lg: "auto"}}}
                    InputProps={{
                        endAdornment:
                            <InputAdornment position="start">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowEnteredPassword(!showEnteredPassword)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    edge="end">
                                    {showEnteredPassword ? <VisibilityOffRounded/> :
                                        <VisibilityRounded/>}
                                </IconButton>
                            </InputAdornment>
                    }}/>
                <LoadingButton
                    loading={pending}
                    type="submit" variant="contained"
                    sx={{ mt: {xs: 1.5, lg: 0}, ml: {lg: 1.5}, py: 1.5, px: {lg: 5.2}, width: {xs: "100%", lg: "auto"}, height: 1 }}>تایید</LoadingButton>
            </Box>
        </Paper>
    )
}
const ExpiredError = () => {
    return (
        <Paper sx={{ p: 2.5, width: 1, minHeight: {xs: "50vw", lg: "25vw"}, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <ErrorOutline color="error" sx={{ fontSize: "4rem" }}/>
            <Typography mt={1.5} fontSize={{ xs: "1.1rem", lg: "1.25rem" }} fontWeight={600}>اشتراک گذاری مورد نظر به پایان رسیده است</Typography>
            <Link to="/"><Button sx={{ mt: 1 }}>بازگشت به صفحه اصلی</Button></Link>
        </Paper>
    )
}
const AccessDenied = () => {
    return (
        <Paper sx={{ p: 2.5, width: 1, minHeight: {xs: "50vw", lg: "25vw"}, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
            <ErrorOutline color="error" sx={{ fontSize: "4rem" }}/>
            <Typography mt={1.5} fontSize={{ xs: "1.1rem", lg: "1.25rem" }} fontWeight={600}>شما به این اشتراک گذاری دسترسی ندارید!</Typography>
            <Link to="/"><Button sx={{ mt: 1 }}>بازگشت به صفحه اصلی</Button></Link>
        </Paper>
    )
}
const LoginToContinue = ({onClick}) => {
    return (
        <Container sx={{ my: 5 }}>
            <Paper sx={{p: 2.5, width: 1, minHeight: {xs: "50vw", lg: "25vw"}, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column"}}>
                <Box display="flex" alignItems="center" justifyContent="center">
                    <LoginRounded color="info" sx={{ fontSize: "2.5rem" }}/>
                    <Typography ml={1} fontSize={{ xs: "1.1rem", lg: "1.25rem" }} fontWeight={600}>نیاز به ورود</Typography>
                </Box>
                <Alert severity="info" sx={{ mt: 1.5 }}>برای دسترسی به این اشتراک گذاری شما باید اول وارد حسابتان شوید</Alert>
                <Button onClick={onClick} variant="contained" sx={{mt: 1.5, py: 1.5, px: {lg: 12}, width: {xs: "100%", lg: "auto"} }}>ورود / ثبت نام</Button>
            </Paper>
        </Container>
    )
}
const LoadingSkeleton = () => {
    return (
        <Container sx={{ my: 5, display: "flex", justifyContent: "space-between", flexDirection: {xs: "column", lg: "row"} }}>
            <Box component="main" flex={1}>
                <Skeleton variant="rounded" sx={{ height: "7rem" }}/>
                <Skeleton variant="rounded" sx={{ mt: 3, height: {xs: "100vw", lg: "50vw"} }}/>
            </Box>
            <Box component="aside" sx={{ mt: { xs: 5, lg: 0 }, ml: { lg: 5 }, width: {xs: "100%", lg: "25%"} }}>
                <Skeleton variant="rounded" sx={{ height: {xs: "50vw", lg: "20vw"} }}/>
                <Skeleton variant="rounded" sx={{ mt: 3, height: {xs: "50vw", lg: "20vw"} }}/>
            </Box>
        </Container>
    )
}